import React, { useCallback, useEffect, useRef, useState } from 'react';
import Question from "../chat/components/question";
import Answer from "../chat/components/answer";
import { getSubscriptionRecordList, getUnReadCount } from '$src/services/fetchs';
import styles from './index.module.scss';
import { useChatStore } from '@/store';

/**
 * 订阅推送记录
 * @returns 
 */
const SubscriptionRecord = ({ id = '', scheduledTaskId = '' }) => {
  const { unReadCount, setUnReadCount } = useChatStore();
  const [subscriptionRecordList, setSubscriptionRecordList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  // 滚动容器引用
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // 分页相关
  const pageIndexRef = useRef(1);
  const pageSize = 20;
  const isLoadingRef = useRef(false);

  useEffect(() => {
    if (unReadCount > 0 && !id) {
      pageIndexRef.current = 1;
      triggerLoadMoreRecords();
    }
  }, [unReadCount, id]);

  const triggerLoadMoreRecords = useCallback(() => {
    setHasMore(true);
    setTimeout(() => {
      loadMoreRecords();
    }, 0);
  }, []);

  // 下拉加载订阅推送记录
  const onScroll = useCallback((e) => {
    const target = e.target;
    // 检查是否滚动到顶部
    if (target.scrollTop <= 0 && !loading && !isLoadingRef.current) {
      triggerLoadMoreRecords();
    }
    // 检查是否滚动到底部
    // if (target.scrollTop + target.clientHeight >= target.scrollHeight - 20 && !loading && !isLoadingRef.current) {
    //   console.log('检查是否滚动到底部', e);
    //   pageIndexRef.current = 1; // 重置页码, 重新加载第一页，获取新数据
    //   triggerLoadMoreRecords();
    // }
  }, [loading]);

  // 加载更多记录
  const loadMoreRecords = useCallback(async () => {
    if (isLoadingRef.current || loading || !hasMore) return;
    
    isLoadingRef.current = true;
    setLoading(true);
    setError('');

    try {
      const currentPage = pageIndexRef.current;
      const response = await getSubscriptionRecordList({ 
        currentPage, 
        pageSize, 
        subscriptionId: id,
        scheduledTaskId,
      });

      if (response.success && response.data) {
        if (currentPage === 1 && !id) {          
          // 滚动到底部
          if (scrollContainerRef.current) {
            setTimeout(() => {
              scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            }, 100);
          }
        }

        if (!id) {
          getUnReadCount().then((res: any) => {
            setUnReadCount(res?.data ?? 0);
          });
        }
        const newRecords = response.data;
        
        // 如果没有新数据，说明已经加载完所有记录
        if (newRecords.length === 0) {
          setHasMore(false);
          return;
        }

        // 处理记录数据，按任务ID（scheduledTaskId）分组
        const hisMap: any[] = [];
        newRecords.forEach((item) => {
          const { scheduledTaskId, roleType } = JSON.parse(JSON.stringify(item));
          let findIndex = hisMap.findIndex((his) => his.scheduledTaskId === scheduledTaskId);
          const oldHis = hisMap.find((his) => his.scheduledTaskId === scheduledTaskId) || {};
          let answer = { ...oldHis.answer };
          let question = { ...oldHis.question };

          if (roleType !== 'user') {
            answer = {
              ...answer,
              ...item,
            }
          } else {
            question = {
              ...question,
              ...item,
            }
          }
          
          const newHis = {
            scheduledTaskId,
            answer,
            question,
          }
          
          if (findIndex >= 0) {
            hisMap.splice(findIndex, 1, newHis)
          } else {
            hisMap.push(newHis)
          }
        });

        // 更新记录列表，新记录添加到顶部
        setSubscriptionRecordList((prevList) => {
          return [...hisMap, ...prevList];
        });

        // 更新页码
        pageIndexRef.current = currentPage + 1;

        // 如果返回的数据少于页面大小，说明没有更多数据了
        if (newRecords.length < pageSize) {
          setHasMore(false);
        }

        // 保持滚动位置
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const oldHeight = container.scrollHeight;
          
          // 使用 requestAnimationFrame 确保DOM更新后再调整滚动位置
          requestAnimationFrame(() => {
            const newHeight = container.scrollHeight;
            container.scrollTop = newHeight - oldHeight;
          });
        }
      } else {
        setError('加载失败，请重试');
      }
    } catch (err) {
      console.error('加载订阅记录失败:', err);
      setError('网络错误，请检查网络连接');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [id, loading, hasMore]);

  // 初始加载
  useEffect(() => {
    // 重置状态
    setSubscriptionRecordList([]);
    setHasMore(true);
    setError('');
    pageIndexRef.current = 1;
    isLoadingRef.current = false;
    
    // 加载第一页数据
    loadMoreRecords();
  }, [id]);

  // 渲染加载状态
  const renderLoadingState = () => {
    if (!loading) return null;
    
    return (
      <div className={styles.loadingState}>
        <div className={styles.loadingSpinner}></div>
        <span>加载中...</span>
      </div>
    );
  };

  // 渲染错误状态
  const renderErrorState = () => {
    if (!error) return null;
    
    return (
      <div className={styles.errorState}>
        <span>{error}</span>
        <button 
          className={styles.retryButton}
          onClick={() => loadMoreRecords()}
        >
          重试
        </button>
      </div>
    );
  };

  // 渲染无更多数据状态
  const renderNoMoreState = () => {
    if (loading || hasMore || subscriptionRecordList.length === 0) return null;
    
    return (
      <div className={styles.noMoreState}>
        <span>没有更多记录了</span>
      </div>
    );
  };

  return (
    <div className={styles.subscriptionRecordBox}>
      {!id && <div className={styles.subscriptionRecordHeader}>
        <svg fill="none" version="1.1" width="11.666666030883789" height="11.666666030883789" viewBox="0 0 11.666666030883789 11.666666030883789">
          <g>
            <path d="M4.083333,0L4.083333,1.1666666L7.583333,1.1666666L7.583333,0L8.75,0L8.75,1.1666666L11.083333,1.1666666C11.405508,1.1666666,11.666666,1.4278367,11.666666,1.75L11.666666,11.083333C11.666666,11.405508,11.405508,11.666666,11.083333,11.666666L0.58333331,11.666666C0.26117003,11.666666,0,11.405508,0,11.083333L0,1.75C0,1.4278367,0.26117003,1.1666666,0.58333331,1.1666666L2.9166665,1.1666666L2.9166665,0L4.083333,0ZM10.5,5.833333L1.1666666,5.833333L1.1666666,10.5L10.5,10.5L10.5,5.833333ZM3.5,7L3.5,8.166666L2.3333333,8.166666L2.3333333,7L3.5,7ZM6.4166665,7L6.4166665,8.166666L5.25,8.166666L5.25,7L6.4166665,7ZM9.333333,7L9.333333,8.166666L8.166666,8.166666L8.166666,7L9.333333,7ZM2.9166665,2.3333333L1.1666666,2.3333333L1.1666666,4.6666665L10.5,4.6666665L10.5,2.3333333L8.75,2.3333333L8.75,3.5L7.583333,3.5L7.583333,2.3333333L4.083333,2.3333333L4.083333,3.5L2.9166665,3.5L2.9166665,2.3333333Z" fill="#FF6700" fillOpacity="1"/>
          </g>
        </svg>
        <span>这是您的订阅记录哦～</span>
      </div>}

      <div className={styles.pushRecordBox}>
        <div 
          ref={scrollContainerRef}
          className={styles.scrollContainer}
          onScroll={onScroll}
        >
          {/* 加载状态 */}
          {renderLoadingState()}
          
          {/* 错误状态 */}
          {renderErrorState()}
          
          {/* 记录列表 */}
          <ul className={styles.recordList}>
            {subscriptionRecordList?.map((subscriptionRecord) => {
              const { answer = {}, question = {}, scheduledTaskId } = subscriptionRecord || {}
              return (answer || question) ? (
                <li key={scheduledTaskId} className={styles.recordItem}>
                  {question ? (<div>
                      <div className={styles.pushTime}>推送时间：{answer.pushTime || answer.executionTime}</div>
                      <Question isTestChat={false} {...question} />
                    </div>
                  ) : null }
                  {answer && !!answer.context ? (
                    <Answer 
                      isTestChat={false} 
                      isHistory 
                      {...answer} 
                      {...answer.context} 
                      askQuestion={() => {}} 
                      thoughtProgressFinished={true} 
                      showGuess={false} 
                    />
                  ) : null}
                </li>
              ) : null
            })}
          </ul>
          
          {/* 无更多数据状态 */}
          {/* {renderNoMoreState()} */}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRecord;